import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SettingsSection } from './-components/settings-section';
import type { BusinessHours } from '@minty/shared/business-hours';
import { useCan } from '~/hooks/use-can';
import {
  createBusinessHoursSchema,
  defaultBusinessHours,
} from '@minty/shared/business-hours';
import { useConfirm } from '~/components/confirm-dialog';
import { Avatar } from '~/components/ui/avatar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Switch } from '~/components/ui/switch';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings/work-hours')({
  pendingComponent: WorkHoursSettingsPending,
  component: WorkHoursSettingsPage,
});

function WorkHoursSettingsPageContent() {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const { data: businessHours } = useSuspenseQuery(
    trpc.settings.businessHours.get.queryOptions(),
  );

  return (
    <SettingsSection.Wrapper>
      <SettingsSection.Header>
        <SettingsSection.Title>
          {t('Office Working Hours')}
        </SettingsSection.Title>
        <SettingsSection.Description>
          {t('Set your office working hours for each day of the week')}
        </SettingsSection.Description>
      </SettingsSection.Header>

      <WorkHoursSettingsForm
        businessHours={
          businessHours ? { days: businessHours.days } : defaultBusinessHours
        }
      />
    </SettingsSection.Wrapper>
  );
}

function WorkHoursSettingsPending() {
  const { t } = useTranslation('settings');

  return (
    <SettingsSection.Wrapper aria-busy="true">
      <SettingsSection.Header>
        <SettingsSection.Title>
          {t('Office Working Hours')}
        </SettingsSection.Title>
        <SettingsSection.Description>
          {t('Set your office working hours for each day of the week')}
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.ContentWrapper>
        <p className="text-sm text-muted-foreground">
          {t('Loading business hours...')}
        </p>
      </SettingsSection.ContentWrapper>
      <SettingsSection.Footer className="justify-end gap-4">
        <SettingsSection.FooterButton type="button" disabled>
          {t('Save')}
        </SettingsSection.FooterButton>
      </SettingsSection.Footer>
    </SettingsSection.Wrapper>
  );
}

function WorkHoursSettingsForm({
  businessHours,
}: {
  businessHours: BusinessHours;
}) {
  const { t } = useTranslation('settings');
  const confirm = useConfirm();
  const trpc = useTRPC();
  const { mutateAsync: saveBusinessHours } = useMutation(
    trpc.settings.businessHours.save.mutationOptions(),
  );

  const form = useForm<BusinessHours>({
    resolver: zodResolver(createBusinessHoursSchema(t)),
    mode: 'onChange',
    defaultValues: businessHours,
    values: businessHours,
    resetOptions: {
      keepDirtyValues: true,
    },
  });
  const days = [
    t('Monday'),
    t('Tuesday'),
    t('Wednesday'),
    t('Thursday'),
    t('Friday'),
    t('Saturday'),
    t('Sunday'),
  ].map((label) => ({
    label,
    shortcut: getDayShortcut(label),
  }));

  const shouldWarnAboutUnsavedChanges =
    form.formState.isDirty && !form.formState.isSubmitting;

  useBlocker({
    // No same-pathname shortcut: signing out reloads the document, which
    // reports `next` as this very page, and must still ask before dropping
    // unsaved hours.
    shouldBlockFn: async () => {
      if (!shouldWarnAboutUnsavedChanges) {
        return false;
      }

      return !(await confirm({
        title: t('You have unsaved changes. Leave without saving?'),
        confirmLabel: t('Leave without saving', { ns: 'translation' }),
        isDestructive: true,
      }));
    },
    enableBeforeUnload: shouldWarnAboutUnsavedChanges,
  });

  async function handleSubmit(values: BusinessHours) {
    try {
      const savedBusinessHours = await saveBusinessHours(values);
      form.reset(savedBusinessHours);
      notify.success(t('Business hours saved.'));
    } catch {
      notify.error(t('Unable to save business hours.'));
    }
  }

  const isSubmitting = form.formState.isSubmitting;

  return (
    <Form.Root form={form} onSubmit={handleSubmit}>
      <SettingsSection.ContentWrapper>
        <div className="mb-2 flex items-center space-x-3 pl-12">
          <label className="block w-60 text-sm font-medium text-foreground">
            {t('Opening')}
          </label>
          <label className="block text-sm font-medium text-foreground">
            {t('Closing')}
          </label>
        </div>
        <div className="space-y-3">
          {days.map((day, index) => (
            <div key={day.label} className="flex items-start space-x-3">
              <Avatar className="flex size-9 items-center justify-center bg-muted text-foreground">
                <span className="text-sm font-bold leading-none">
                  {day.shortcut}
                </span>
              </Avatar>

              <SettingsSection.Input.Wrapper className="w-60">
                <FormField
                  control={form.control}
                  name={`days.${index}.startTime`}
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <Input
                          type="time"
                          className="text-sm font-normal"
                          aria-label={`${day.label} ${t('Opening')}`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </SettingsSection.Input.Wrapper>

              <SettingsSection.Input.Wrapper className="w-60">
                <FormField
                  control={form.control}
                  name={`days.${index}.endTime`}
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <Input
                          type="time"
                          className="text-sm font-normal"
                          aria-label={`${day.label} ${t('Closing')}`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </SettingsSection.Input.Wrapper>

              <FormField
                control={form.control}
                name={`days.${index}.isOpen`}
                render={({ field }) => (
                  <FormItem className="pt-2.5">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                        aria-label={day.label}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ))}
        </div>
      </SettingsSection.ContentWrapper>

      <SettingsSection.Footer className="justify-end gap-4">
        <SettingsSection.FooterButton type="submit" disabled={isSubmitting}>
          {isSubmitting && (
            <Loader2 className="animate-spin" strokeWidth={2.5} />
          )}
          {t('Save')}
        </SettingsSection.FooterButton>
      </SettingsSection.Footer>
    </Form.Root>
  );
}

function getDayShortcut(dayLabel: string) {
  return dayLabel.slice(0, 2).toLocaleUpperCase();
}

function WorkHoursSettingsPage() {
  const allowed = useCan('businessHours');
  return allowed ? <WorkHoursSettingsPageContent /> : null;
}
